import moment from "moment-timezone";
import type {
  IAuction,
  ICurrency,
  ILot,
  IActiveLot,
  IUser,
  IHistoryLog,
  ILiveSettings,
  ILotPopupData,
  ILanguage
} from "@/models";
import { BiddingSteps } from "@/enum";
import { defineStore } from "pinia";
import Api from "@/services/api";
import { updateActiveLotListeners } from "@/utils/websockets";
import { trans } from "@/utils/trans";
import { IUserListItem } from "@/models/api/user/IUserListItem";
import { IMenuItem, IMessage, IFilePlaceholders } from "@/models";
import { watch, computed } from "vue";
import { loadNotifications, setNotificationsChannel } from "@/plugins/notifications";

export const useAuctionStore = defineStore("auction", {
  state: () => {
    return {
      routes: <Record<string, string>>{},
      translations: <Record<string, never> | null>null,

      auctionsTimezone: "" as string,
      localTimezone: "" as string,

      currency: "USD",
      isCurrencyBase: true,
      currencies: [] as ICurrency[],
      languageSettings: {
        language: "en" as string | null,
        htmlDirection: "ltr" as string,
        languages: [] as ILanguage[]
      },

      auction: {} as IAuction,
      user: null as IUser | null,
      lots: <ILot[]>[],
      fakeActiveLot: {} as IActiveLot | null,
      activeLot: {} as IActiveLot | null,
      popupLot: null as ILotPopupData | null,
      historyLogs: <IHistoryLog[]>[],
      userOnLive: [] as IUserListItem[],
      userOnLiveAnonymous: 0,
      resetTimeLineTrigger: false,
      syncTimeLineTrigger: false,
      refreshStepButtonTrigger: false,
      serverTimeOffsetMs: 0,
      hasServerTimeOffset: false,
      activeLotRealtimeUpdatedAt: null as number | null,

      filePlaceholders: {} as IFilePlaceholders,
      isLockedBidding: false,
      isLockedButtons: false,

      liveAuctionListener: null as string | null,
      liveAuctionBiddingStepListener: null as string | null,
      activeLotListener: null as string | null,
      catalogLotsListener: null as string | null,
      currentBidListener: null as string | null,
      popupCurrentBidListener: null as string | null,
      popupBidsCountListener: null as string | null,
      leaderChangedListener: null as string | null,
      leadersListUpdatedListener: null as string | null,
      bidsCountListener: null as string | null,
      bookBidListener: null as string | null,
      userBidsListener: null as string | null,
      sellerEventsListener: null as string | null,
      userEventsListener: null as string | null,
      liveChatListener: null as string | null,
      auctionSettingsListener: null as string | null,

      liveSettings: null as ILiveSettings | null,
      menu: null as IMenuItem | null,
      isMobileMenuOpen: false,
      pinMessage: null as IMessage | null,
      newMessagePinSeconds: 60,
      notifications: [] as any[],

      // for Admin
      userData: <Record<number, IUserListItem>>{},
      websocketChannels: <Record<string, any>>{},
      pageIsActive: true,
      hasNewChatMessage: false,
      catalog: {
        searchString: null as string | null,
        searchTimeoutId: null as number | null,
        searchLots: [] as ILot[]
      },

      // for Modals
      countOpenedModals: 0
    };
  },
  actions: {
    async fetchAuction() {
      console.log("fetchAuction");
      const slug = this.getSlugByUrl();
      if (slug.length) {
        try {
          const requestStartedAt = Date.now();
          const response = await Api.get("/live/auctions/get-data", {
            params: { slug }
          });
          const responseReceivedAt = Date.now();

          if (response.data.success) {
            this.syncServerTime(response.data.server_time, requestStartedAt, responseReceivedAt);

            this.$patch((state) => {
              state.routes = <Record<string, string>>response.data.routes;
              state.translations = <Record<string, never> | null>response.data.translations;
              state.currencies = <ICurrency[]>response.data.app_data.currencies;
              state.auction = <IAuction>response.data.auction;
              state.user = response.data.user ? <IUser>response.data.user : null;
              state.filePlaceholders = <IFilePlaceholders>response.data.app_data.file_placeholders;
              state.liveSettings = <ILiveSettings>response.data.live_settings;
              state.auctionsTimezone = <string>response.data.app_data.auctions_timezone;
              state.newMessagePinSeconds = <number>response.data.app_data?.new_message_pin_time || 60;

              state.languageSettings.language = response.data.app_data?.language ?? "en";
              state.languageSettings.languages = response.data.app_data?.languages || [];
              state.languageSettings.htmlDirection = response.data.app_data?.html_direction || "ltr";

              state.localTimezone = moment.tz.guess();

              if (response.data.active_lot) {
                this.setActiveLot(response.data.active_lot);
                this.refreshHistoryLogs(response.data.active_lot_logs.logs);
              } else {
                state.activeLot = null as IActiveLot | null;
                state.fakeActiveLot = null as IActiveLot | null;
                state.historyLogs = [];
              }
            });
          }
        } catch (error) {
          console.error("Cannot get auction", error);
        }
      }
    },
    async fetchMenu() {
      if (this.menu) return;
      try {
        const response = await Api.get("/live/auctions/get-menu");
        if (response.data.menu) {
          this.$patch((state) => {
            state.menu = <IMenuItem>response.data.menu;
          });
        }
      } catch (error) {
        console.error("Cannot get menu", error);
      }
    },
    fetchLots(params = {}) {
      console.log("fetchLots");
      if (this.auction?.id) {
        Api.get("/live/lots", {
          params: {
            resourceType: "auction",
            resourceId: this.auction.id,
            ...params
          }
        })
          .then((response) => {
            this.$patch((state) => {
              state.lots = <ILot[]>response.data.data;
            });

            if (!this.activeLot?.id) {
              // Find first appropriate
              this.fetchNextActiveLot()
                .then((response) => {
                  if (response.data.success && response.data.next_active_lot) {
                    this.$patch((state) => {
                      state.fakeActiveLot = response.data.next_active_lot;
                    });
                  }
                })
                .catch((error) => {
                  console.log(error);
                });
            }
          })
          .catch((error) => {
            console.error("Cannot get lots", error);
          });
      }
    },
    initFilter() {
      watch(
        () => this.catalog.searchString,
        () => {
          if (this.catalog.searchTimeoutId) {
            clearTimeout(this.catalog.searchTimeoutId);
            this.catalog.searchTimeoutId = null;
          }
          this.catalog.searchTimeoutId = setTimeout(() => this.searchLots(), 600);
        }
      );
    },
    searchLots() {
      debugger;
      const params = <{ searchTextLive?: string }>{};
      if (this.catalog.searchString?.length) {
        params.searchTextLive = this.catalog.searchString;

        if (this.auction?.id) {
          Api.get("/live/lots", {
            params: {
              resourceType: "auction",
              resourceId: this.auction.id,
              ...params
            }
          })
            .then((response) => {
              this.$patch((storeState) => {
                storeState.catalog.searchLots = response.data.data;
              });
            })
            .catch((error) => {
              console.error("Cannot get search lots", error);
            });
        }
      }
    },
    startAuction(auction: IAuction) {
      return Api.post("/live/auctions/" + auction.id + "/start");
    },
    activateLot(lot: ILot) {
      return Api.post("/live/lots/" + lot.id + "/activate");
    },
    reopenLot(lot: ILot) {
      return Api.post("/live/lots/" + lot.id + "/reopen");
    },
    closeLot(lot: ILot) {
      return Api.post("/live/lots/" + lot.id + "/close");
    },
    fetchNextActiveLot() {
      return Api.get("/live/auctions/" + this.auction.id + "/get-next-active-lot");
    },
    async syncActiveLotSnapshot() {
      if (!this.auction?.id) {
        return;
      }

      try {
        const requestStartedAt = Date.now();
        const response = await Api.get("/live/auctions/" + this.auction.id + "/sync-active-lot", {
          params: {
            client_active_lot_id: this.activeLot?.id ?? ""
          }
        });
        const responseReceivedAt = Date.now();

        if (response.data.success) {
          this.syncServerTime(response.data.server_time, requestStartedAt, responseReceivedAt);

          if (this.activeLotWasUpdatedAfter(requestStartedAt)) {
            return;
          }

          if (response.data.is_current) {
            return;
          }

          this.$patch((state) => {
            if (response.data.active_lot) {
              if (this.activeLotIsNewerThan(response.data.active_lot)) {
                return;
              }

              this.setActiveLot(response.data.active_lot);
              this.refreshHistoryLogs(response.data.active_lot_logs.logs);
            } else {
              if (this.activeLotWasUpdatedAfter(requestStartedAt)) {
                return;
              }

              state.activeLot = null as IActiveLot | null;
              state.fakeActiveLot = null as IActiveLot | null;
              state.historyLogs = [];
            }
          });
        }
      } catch (error) {
        console.error("Cannot get active lot snapshot", error);
      }
    },
    activeLotIsNewerThan(activeLot: IActiveLot) {
      const currentUpdatedAt = this.getTimestampMs(this.activeLot?.live_step_updated_at ?? null);
      const incomingUpdatedAt = this.getTimestampMs(activeLot.live_step_updated_at);

      return currentUpdatedAt !== null && incomingUpdatedAt !== null && currentUpdatedAt > incomingUpdatedAt;
    },
    activeLotWasUpdatedAfter(clientTimestampMs: number) {
      if (this.activeLotRealtimeUpdatedAt !== null && this.activeLotRealtimeUpdatedAt > clientTimestampMs) {
        return true;
      }

      const currentUpdatedAt = this.getTimestampMs(this.activeLot?.live_step_updated_at ?? null);

      if (currentUpdatedAt === null) {
        return false;
      }

      const serverTimestampMs = clientTimestampMs + (this.hasServerTimeOffset ? this.serverTimeOffsetMs : 0);
      return currentUpdatedAt > serverTimestampMs;
    },
    getTimestampMs(value: string | Date | null) {
      if (!value) {
        return null;
      }

      const timestamp = new Date(value).getTime();
      return Number.isNaN(timestamp) ? null : timestamp;
    },
    markActiveLotRealtimeUpdated() {
      this.activeLotRealtimeUpdatedAt = Date.now();
    },

    resetTimeLine(liveStepUpdatedAt: string | Date | null = null) {
      if (liveStepUpdatedAt && this.activeLot?.id) {
        this.activeLot.live_step = BiddingSteps.NONE;
        this.activeLot.live_step_updated_at = liveStepUpdatedAt;
        this.syncTimeLine();
        return;
      }

      this.resetTimeLineTrigger = !this.resetTimeLineTrigger;
    },
    syncTimeLine() {
      this.syncTimeLineTrigger = !this.syncTimeLineTrigger;
    },
    syncServerTime(serverTime: string | Date | null, requestStartedAt = Date.now(), responseReceivedAt = requestStartedAt) {
      if (!serverTime) {
        return;
      }

      const serverTimeMs = new Date(serverTime).getTime();

      if (Number.isNaN(serverTimeMs)) {
        return;
      }

      const roundTripMs = Math.max(responseReceivedAt - requestStartedAt, 0);

      if (roundTripMs > 1000) {
        this.hasServerTimeOffset = false;
        return;
      }

      this.serverTimeOffsetMs = serverTimeMs - (requestStartedAt + roundTripMs / 2);
      this.hasServerTimeOffset = true;
    },
    getServerNowMs() {
      return Date.now() + (this.hasServerTimeOffset ? this.serverTimeOffsetMs : 0);
    },
    refreshStepButton() {
      this.refreshStepButtonTrigger = !this.refreshStepButtonTrigger;
    },
    // Variant B (#586): the live server tells us when the current step's auto-advance
    // timer actually started. Re-anchor the active lot so the button fill matches the
    // real countdown (the watcher on live_step_started_at recomputes the fill).
    setStepTimerStartedAt(startedAt: string | Date | null, step: number | null = null) {
      // Apply only if this timer is for the step currently shown. Otherwise it's a
      // cross-channel race (step_timer arrived before the step change) — ignore it so
      // the outgoing button isn't briefly re-anchored to the next step's timer (#586).
      if (this.activeLot?.id && startedAt && this.activeLot.live_step === step) {
        this.markActiveLotRealtimeUpdated();
        this.activeLot.live_step_started_at = startedAt;
      }
    },

    setActiveLot(activeLot: IActiveLot) {
      if (!activeLot?.id) {
        return;
      }

      this.$patch((state) => {
        state.activeLot = activeLot;
      });
      updateActiveLotListeners(activeLot);
    },
    setActiveLotBookBid(bookBid: number) {
      if (this.activeLot?.id) {
        this.activeLot.book_bid = bookBid;
      }
    },
    setCurrentStep(
      step: BiddingSteps,
      liveStepUpdatedAt: string | Date | null = null,
      liveStepStartedAt: string | Date | null = null
    ) {
      if (this.activeLot?.id) {
        const hasStepChanged = this.activeLot.live_step !== step;

        if (hasStepChanged) {
          this.activeLot.live_step = step;
        }

        if (liveStepUpdatedAt) {
          this.activeLot.live_step_updated_at = liveStepUpdatedAt;
        }

        if (liveStepStartedAt) {
          this.activeLot.live_step_started_at = liveStepStartedAt;
        }

        // Re-sync the progress bar only on lot open / bid reset (NONE). The bar is
        // anchored to lot-open and runs continuously; re-syncing it on every step
        // makes the countdown digit flicker at the boundary (#586).
        if (step === BiddingSteps.NONE) {
          this.syncTimeLine();
        }
      }
    },
    filteredBidVariants() {
      const startingPrice = this.activeLot?.starting_price;
      const currentBid = this.activeLot?.current_bid ?? 0;
      const bidsCount = this.activeLot?.bids_count ?? 0;
      const bookBid = this.activeLot?.book_bid ?? 0;
      const userBid = this.activeLot ? this.user?.bids[this.activeLot.id] ?? 0 : 0;

      if (this.auction.bid_on_starting_price && currentBid === startingPrice && bidsCount === 0) {
        return this.auction.bid_variants.filter((item) => item >= currentBid && item > userBid && item > bookBid);
      } else {
        return this.auction.bid_variants.filter((item) => item > currentBid && item > userBid && item > bookBid);
      }
    },
    addBidVariants() {
      let newVariant = Math.max(...this.auction.bid_variants);

      for (let i = 0; i < 100; i++) {
        newVariant += this.auction.bid_infinite_step;
        this.auction.bid_variants.push(newVariant);
      }
    },
    resetHistoryLog() {
      this.historyLogs = [];
    },
    addHistoryLog(log: IHistoryLog, isRecent = false) {
      const preparedLog = { ...log, is_recent: isRecent };

      if (this.user && preparedLog.bidder_id && preparedLog.bidder_full_name && this.user.id === preparedLog.bidder_id) {
        preparedLog.is_your_bid = true;

        const yourBidText = log.type === 'Sold' ? trans("live.logs.you") : trans("live.logs.your_bid");
        const replaceKeys = trans("live.logs.your_bid_replace").split("|");

        replaceKeys.forEach((replacePattern: string) => {
          const search = replacePattern.replace(":bidder", preparedLog.bidder_full_name ?? "");
          preparedLog.text = preparedLog.text.replace(search, yourBidText);
        });
      }

      const existingLogIndex = this.historyLogs.findIndex((item) => item.id === preparedLog.id);
      if (existingLogIndex === -1) {
        this.historyLogs.push(preparedLog);
      } else {
        this.historyLogs.splice(existingLogIndex, 1, preparedLog);
      }
    },
    refreshHistoryLogs(logs: IHistoryLog[]) {
      this.historyLogs = [];
      logs.forEach((log) => this.addHistoryLog(log));
    },
    setBreak() {
      return Api.post("/live/auctions/" + this.auction.id + "/toggle-break");
    },
    getSlugByUrl() {
      const currentUrl = window.location.href;
      const parsedUrl = new URL(currentUrl);
      const pathSegments = parsedUrl.pathname.split("/").filter(Boolean);
      const auctionSegmentIndex = pathSegments.indexOf("auction");

      let slug = "";
      if (auctionSegmentIndex !== -1 && auctionSegmentIndex + 1 < pathSegments.length) {
        slug = pathSegments[auctionSegmentIndex + 1];
      }

      return slug;
    },
    async initNotifications() {
      if (this.user?.id) {
        try {
          const notifications = await loadNotifications();
          this.setNotifications(notifications.data.data);
        } catch (error) {
          console.error("Failed to load notifications:", error);
        }

        setNotificationsChannel(this.user.id).notification((notification: any) => {
          this.addNotification(notification);
        });
      }
    },
    addNotification(value: any) {
      this.notifications.unshift(value);
    },
    setNotifications(value: any) {
      this.notifications = value;
    }
  }
});
